import * as React from 'react'
import { Box, Button } from '@mui/material'
import { contentFiles, uploadContentFile } from '../ingestion/Utility'
import { FileSelector } from '../FileSelector'
import { notFolders, copyToClipboard } from 'shared/utils'
import { PartnerUploadContext } from 'ingestion/PartnerContext'
import { AwsContentItem } from 'shared/types/ingestion'

export function ContentUploadIndex({
  goToPartner,
}: {
  goToPartner: (params: {
    partner: string
    currId: string
    gradeCode: string
  }) => void
}) {
  const partnerIndex = [
    {
      title: 'CKLA',
      partner: 'ckla',
      currId: 'curricula',
      gradeCode: '7',
    },
    {
      title: 'EL',
      partner: 'el',
      currId: 'curricula',
      gradeCode: '7',
    },
    {
      title: 'OUR',
      partner: 'OUR',
      currId: 'Math',
      gradeCode: '7',
    },
  ]
  return (
    <Box>
      <Box>Content Upload</Box>
      <Box>
        {partnerIndex.map((partner, i) => (
          <Button key={i} onClick={() => goToPartner(partner)}>
            {partner.title}
          </Button>
        ))}
      </Box>
    </Box>
  )
}

export function PartnerContentUploadFromContext() {
  const { subject, partnerKey, environment, gradeCode } =
    React.useContext(PartnerUploadContext)
  if (!subject || !partnerKey || !gradeCode) return null

  return (
    <PartnerContentUpload
      envOverride={environment}
      partner={partnerKey}
      subject={subject}
      gradeCode={gradeCode}
    />
  )
}

export function PartnerContentUpload({
  partner,
  subject,
  gradeCode,
  envOverride,
}: {
  partner: string
  subject: string
  gradeCode: string
  envOverride?: string
}) {
  const [toUpload, setToUpload] = React.useState<{
    files?: File[]
    webkitFiles?: any
  }>({})
  const [existingFiles, setExistingFiles] = React.useState<{
    [key: string]: AwsContentItem
  }>({})
  const [recentlyAdded, setRecentlyAdded] = React.useState<{
    [key: string]: string
  }>({})
  const [showAll, setShowAll] = React.useState(false)

  React.useEffect(() => {
    contentFiles({
      partnerKey: partner,
      subject,
      curricula: gradeCode,
      envOverride,
    }).then(setExistingFiles)
  }, [partner, subject, gradeCode, envOverride])

  function params() {
    return {
      partnerKey: partner,
      subject,
      curricula: gradeCode,
      envOverride,
    }
  }

  React.useEffect(() => {
    const checker = (f: { name: string }) => {
      const existing = existingFiles[f.name]
      if (existing) {
        setRecentlyAdded((prev) => ({
          ...prev,
          [existing.key]: existing.URL,
        }))
      }
    }
    toUpload.webkitFiles?.forEach(checker)
    toUpload.files?.forEach(checker)
  }, [existingFiles, toUpload])

  function fileSelected(files: File[], asWebKit: any[]) {
    if (asWebKit[0]?.isDirectory) {
      // set folder for uploading
      notFolders(asWebKit[0]).then((webkitFiles) => {
        setToUpload({ webkitFiles })
      })
    } else {
      // filter out all folders, upload only files
      const filtered: File[] = files.filter((f, i) => !asWebKit[i]?.isDirectory)
      setToUpload({ files: filtered })
    }
  }

  function uploadFiles() {
    const onComplete = (url: string, fileName: string) => {
      setExistingFiles((prev) => ({
        ...prev,
        [fileName]: {
          URL: url,
          key: fileName,
          last_modified: new Date().toLocaleString(),
        },
      }))
      setRecentlyAdded((prev) => ({ ...prev, [fileName]: url }))
    }
    toUpload.files?.reduce((acc, file) => {
      return acc.then(() => {
        return uploadContentFile({
          ...params(),
          blob: file,
          fileName: file.name,
        }).then((resp) => {
          onComplete(resp, file.name)
        })
      })
    }, Promise.resolve())
    toUpload.webkitFiles?.reduce((acc: Promise<void>, webKit: any) => {
      return new Promise((resolve) => {
        webKit.file((file: File) => {
          uploadContentFile({
            ...params(),
            blob: file,
            fileName: file.name,
          })
            .then((resp) => {
              onComplete(resp, file.name)
            })
            .then(resolve)
        })
      })
    }, Promise.resolve())
  }

  return (
    <Box>
      <Box>{partner}</Box>
      <Box>{subject}</Box>
      <Box>Grade: {parseInt(gradeCode) - 1}</Box>
      <Box>
        Source File
        <FileSelector allowMultiple={true} filesSelected={fileSelected} />
        <Button disabled={!toUpload} onClick={uploadFiles}>
          Upload Files
        </Button>
      </Box>
      <Box padding={'5px'} border={'solid 1px'}>
        {Object.keys(recentlyAdded).map((name) => (
          <UrlCopyComponent key={name} name={name} url={recentlyAdded[name]} />
        ))}
      </Box>
      <Box padding={'5px'}>
        <Button onClick={() => setShowAll(!showAll)}>
          Show All {Object.keys(existingFiles).length} Existing Files
        </Button>
        {showAll && <ExistingFiles existing={existingFiles} />}
      </Box>
    </Box>
  )
}

function ExistingFiles({
  existing,
}: {
  existing: { [key: string]: AwsContentItem }
}) {
  return (
    <Box>
      {Object.keys(existing)
        .sort()
        .map((key) => {
          return (
            <UrlCopyComponent key={key} name={key} url={existing[key].URL} />
          )
        })}
    </Box>
  )
}

function UrlCopyComponent({ name, url }: { name: string; url: string }) {
  if (!name) return null
  function copy() {
    copyToClipboard(url)
  }
  return (
    <Box>
      <a href={url} target="_blank" rel="noreferrer">
        {name}
      </a>
      <Button onClick={() => copy()}>Copy</Button>
    </Box>
  )
}
